import { useCallback, useRef } from 'react';
import * as boardsStore from '../../extensions/bundled/task-manager/boardsStore';
import { arrayMove } from '@dnd-kit/sortable';
import { boardArrangementKey } from '../../extensions/bundled/task-manager/boardReconcile';
import { touchedEntityIds } from '../../extensions/bundled/task-manager/boardHistoryPatch';
import { closestCenter, closestCorners } from '@dnd-kit/core';

/**
 * Drag and drop for the open board, Kanban and List alike: the per-frame
 * reorder, the single undo entry per drag, and the replay of sync work that
 * arrived mid-gesture. Moved out of TaskBoardTab unchanged.
 */
export default function useBoardDrag({
  applyBoardRecordsRef,
  boardRef,
  dragActiveRef,
  dragUndoSnapshotRef,
  flushIfTeamBoard,
  markDirtyEntity,
  moveTaskToNewList,
  pendingBoardChangesRef,
  pendingBoardsBumpRef,
  pushUndo,
  reconcileFromDiskRef,
  setActiveDragId,
  syncHistoryFlags,
  updateBoard,
}) {
  // Live multi-container reordering: both group and task moves are applied
  // on every onDragOver (not just at drop), so the board's own re-render is
  // the "placeholder" — sibling cards/columns visibly shift to open a gap at
  // the position the drag would land at. onDragEnd only needs to clear the
  // active-drag id; by the time it fires, `board` already reflects the final
  // position from the last onDragOver call. This also fixes the old
  // onDragEnd-only algorithm's occasional "drop doesn't register" bug, since
  // state is now kept continuously in sync with the pointer instead of being
  // recomputed once, fragile, at drop time.
  // The actual reorder. Called at most once per animation frame (see
  // handleDragOver) so a fast drag near a column boundary can't flip-flop the
  // board state synchronously — that re-entrancy is what tripped @dnd-kit's
  // measuring into "Maximum update depth exceeded" under React 19.
  const applyDragMove = useCallback(
    (activeId, overId) => {
      if (!overId || activeId === overId) return;

      if (activeId.startsWith('group:')) {
        updateBoard((prev) => {
          // closestCorners often lands on a card (`task:`) or column body
          // (`col:`) inside the target column rather than its `group:`
          // sortable — resolve whatever we're over to its owning group, or
          // group drags only register intermittently.
          let overGroupId = null;
          if (overId.startsWith('group:')) {
            overGroupId = overId.slice(6);
          } else if (overId.startsWith('col:')) {
            overGroupId = overId.slice(4);
          } else if (overId.startsWith('task:')) {
            const overTask = prev.tasks.find((t) => t.id === overId.slice(5));
            overGroupId = overTask ? overTask.groupId : null;
          }
          if (!overGroupId) return prev;
          const groups = [...prev.groups].sort(boardsStore.compareGroupsByPosition);
          const oldIndex = groups.findIndex((g) => `group:${g.id}` === activeId);
          const newIndex = groups.findIndex((g) => g.id === overGroupId);
          if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
          const reordered = arrayMove(groups, oldIndex, newIndex).map((g, i) => ({ ...g, position: i }));
          return { ...prev, groups: reordered };
        }, { history: false }); // per-frame during a drag; one undo entry at drag end
        return;
      }

      if (!activeId.startsWith('task:')) return;
      const taskId = activeId.slice(5);

      updateBoard((prev) => {
        const task = prev.tasks.find((t) => t.id === taskId);
        if (!task) return prev;

        let targetGroupId = null;
        let overTaskId = null;
        if (overId.startsWith('task:')) {
          overTaskId = overId.slice(5);
          const overTask = prev.tasks.find((t) => t.id === overTaskId);
          if (!overTask) return prev;
          targetGroupId = overTask.groupId;
        } else if (overId.startsWith('col:')) {
          targetGroupId = overId.slice(4);
        } else {
          return prev;
        }

        const byGroup = new Map();
        for (const t of prev.tasks) {
          if (!byGroup.has(t.groupId)) byGroup.set(t.groupId, []);
          byGroup.get(t.groupId).push(t);
        }
        for (const list of byGroup.values()) list.sort(boardsStore.compareTasksByPosition);

        const sourceList = byGroup.get(task.groupId) || [];
        const sourceIdx = sourceList.findIndex((t) => t.id === taskId);
        if (sourceIdx === -1) return prev;
        const sameGroup = targetGroupId === task.groupId;

        if (sameGroup) {
          // Same column: reorder with arrayMove using the over item's *current*
          // index, so it matches @dnd-kit's own placeholder (verticalList/rect
          // sorting strategies sort via arrayMove(activeIndex, overIndex)). The
          // old splice landed the item one short — e.g. first-onto-last stopped
          // in the middle.
          let destIdx;
          if (overTaskId) {
            destIdx = sourceList.findIndex((t) => t.id === overTaskId);
            if (destIdx === -1) destIdx = sourceList.length - 1;
          } else {
            destIdx = sourceList.length - 1; // dropped over the column body → end
          }
          if (destIdx === sourceIdx) return prev;
          byGroup.set(task.groupId, arrayMove(sourceList, sourceIdx, destIdx));
        } else {
          const destList = byGroup.get(targetGroupId) || [];
          let destIdx;
          if (overTaskId) {
            destIdx = destList.findIndex((t) => t.id === overTaskId);
            if (destIdx === -1) destIdx = destList.length;
          } else {
            destIdx = destList.length;
          }
          sourceList.splice(sourceIdx, 1);
          const insertAt = Math.min(Math.max(destIdx, 0), destList.length);
          destList.splice(insertAt, 0, { ...task, groupId: targetGroupId });
          byGroup.set(targetGroupId, destList);
          byGroup.set(task.groupId, sourceList);
        }

        const nextTasks = [];
        for (const [gid, list] of byGroup) {
          // Same object for a task the step did not move: memoised cards then
          // skip re-rendering (owner, 13.10.2026: big boards).
          list.forEach((t, i) => nextTasks.push(t.groupId === gid && t.position === i ? t : { ...t, groupId: gid, position: i }));
        }
        return { ...prev, tasks: nextTasks };
      }, { history: false }); // per-frame during a drag; one undo entry at drag end
    },
    [updateBoard]
  );

  const dragOverRafRef = useRef(null);
  const pendingOverRef = useRef(null);

  const cancelPendingDragMove = useCallback(() => {
    if (dragOverRafRef.current != null) {
      cancelAnimationFrame(dragOverRafRef.current);
      dragOverRafRef.current = null;
    }
    pendingOverRef.current = null;
  }, []);

  // Coalesce dragOver events to one reorder per frame (breaks the synchronous
  // measure → collision → reorder → measure loop @dnd-kit + React 19 hit).
  const handleDragOver = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over) return;
      pendingOverRef.current = { activeId: String(active.id), overId: String(over.id) };
      if (dragOverRafRef.current != null) return;
      dragOverRafRef.current = requestAnimationFrame(() => {
        dragOverRafRef.current = null;
        const p = pendingOverRef.current;
        if (p) applyDragMove(p.activeId, p.overId);
      });
    },
    [applyDragMove]
  );

  // Snapshot the board at drag start so the drag (which mutates the board every
  // frame via history:false updateBoard calls) can be undone as one step.
  const handleDragStart = useCallback((event) => {
    dragUndoSnapshotRef.current = boardRef.current;
    dragActiveRef.current = true; // sync re-reads defer until the gesture ends
    setActiveDragId(String(event.active.id));
  }, [boardRef, dragActiveRef, dragUndoSnapshotRef, setActiveDragId]);

  // Drag finished: replay a sync re-read that arrived mid-drag (the
  // boardsVersion effect deferred it). Shared tail of handleDragEnd/Cancel.
  const finishDragAndReplaySync = useCallback(() => {
    dragActiveRef.current = false;
    // Drain the buffer FIRST: the reconcile below flushes this tab's whole-board
    // snapshot before it re-reads, and a record that is not in the snapshot by
    // then is tombstoned by that flush.
    if (pendingBoardChangesRef.current.length) {
      const buffered = pendingBoardChangesRef.current;
      pendingBoardChangesRef.current = [];
      for (const [scopeKey, records] of buffered) {
        applyBoardRecordsRef.current?.(scopeKey, records);
      }
    }
    if (pendingBoardsBumpRef.current) {
      pendingBoardsBumpRef.current = false;
      reconcileFromDiskRef.current?.();
    }
  }, [applyBoardRecordsRef, dragActiveRef, pendingBoardChangesRef, pendingBoardsBumpRef, reconcileFromDiskRef]);

  const handleDragEnd = useCallback(
    (event) => {
      // finally: the drag-active flag MUST clear even if the drop work throws;
      // a stuck flag would defer every future sync re-read (including remote
      // board deletions) until some later drag completes.
      try {
        cancelPendingDragMove();
        const { active, over } = event;
        const activeId = String(active.id);
        if (over && String(over.id) === 'new-list' && activeId.startsWith('task:')) {
          moveTaskToNewList(activeId.slice(5));
        } else if (over) {
          applyDragMove(activeId, String(over.id));
        }
        // Protect the dragged entity from the WS fast path until this drop's
        // flushIfTeamBoard() below actually lands — every applyDragMove frame
        // ran with dragActiveRef true (fully skipped realtime already), but
        // that flag clears in the `finally` below, opening a small window
        // before the flush completes. (Reordered siblings' positions aren't
        // separately protected — a minor, self-healing gap: the disk-reconcile
        // path corrects them once this drop's write lands.)
        if (activeId.startsWith('task:')) markDirtyEntity(boardRef.current?.id, activeId.slice(5));
        else if (activeId.startsWith('group:')) markDirtyEntity(boardRef.current?.id, activeId.slice(6));
        // Commit exactly one undo entry for the whole drag, but only if the card/
        // column arrangement actually changed. Identity comparison won't do: every
        // drag frame runs updateBoard, which mints a fresh object even when the
        // reducer returns prev unchanged — so a card dragged back to its origin
        // would otherwise leave a no-op undo step. Compare drag-mutable placement.
        const before = dragUndoSnapshotRef.current;
        if (before && boardArrangementKey(before) !== boardArrangementKey(boardRef.current)) {
          // Every card the drop re-positioned, not just the dragged one.
          markDirtyEntity(boardRef.current?.id, touchedEntityIds(before, boardRef.current));
          pushUndo({ before, after: boardRef.current });
          syncHistoryFlags();
        }
        // The drop is the one moment a drag gesture "settles" — every frame up
        // to here ran on the debounce (see applyDragMove/handleDragOver) to
        // avoid flooding writes mid-gesture, but the finished move should hit
        // the wire immediately, same as any other discrete edit.
        flushIfTeamBoard();
      } finally {
        dragUndoSnapshotRef.current = null;
        setActiveDragId(null);
        finishDragAndReplaySync();
      }
    },
    [cancelPendingDragMove, markDirtyEntity, boardRef, dragUndoSnapshotRef, flushIfTeamBoard, moveTaskToNewList, applyDragMove, pushUndo, syncHistoryFlags, setActiveDragId, finishDragAndReplaySync]
  );

  const handleDragCancel = useCallback(() => {
    try {
      cancelPendingDragMove();
    } finally {
      dragUndoSnapshotRef.current = null;
      setActiveDragId(null);
      finishDragAndReplaySync();
    }
  }, [cancelPendingDragMove, dragUndoSnapshotRef, finishDragAndReplaySync, setActiveDragId]);

  // Group drags must only ever "see" the group sortables. With plain
  // closestCorners the winner alternates between a neighbor column's cards,
  // its `col:` drop zone, and its `group:` sortable as the pointer moves —
  // the placeholder stutters, and the drop-time `over` can compute the
  // inverse move and snap the column back. Tasks keep closestCorners over
  // everything (they need cards + column bodies as targets).
  const collisionDetectionStrategy = useCallback((args) => {
    if (String(args.active.id).startsWith('group:')) {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((c) => String(c.id).startsWith('group:')),
      });
    }
    return closestCorners(args);
  }, []);

  return { handleDragOver, handleDragStart, handleDragEnd, handleDragCancel, collisionDetectionStrategy };
}
